import React, { useCallback, useState, useEffect, useRef } from 'react';
import _isPlainObject from 'lodash/isPlainObject';
import _isArray from 'lodash/isArray';

import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { FORM_SCHEMA, DEFAULT_FORM_DATA } from './stubs';
import {
  createFormData,
  createPayload,
  getProductVariantIds,
  getProductTypeIds
} from './utils';

/**
 * @type {React.Context<{
 *  mode: import('utils/types').FormMode
 *  windowId: string,
 *  data: import('zod').infer<typeof FORM_SCHEMA>,
 *  initialData: import('zod').infer<typeof FORM_SCHEMA>,
 *  isError: boolean,
 *  isLoading: boolean,
 *  errors: Partial<import('zod').infer<typeof FORM_SCHEMA>>,
 *  isFormOpen: boolean,
 *  isDirty: boolean,
 *  isProcessing: boolean,
 *  selectedType: keyof typeof import('../stubs').PRODUCT_WINDOW_TYPE,
 *  onChange: (updates: Partial<import('zod').infer<typeof FORM_SCHEMA>>) => void,
 *  onLoad: () => void,
 *  onSave: () => void,
 *  onOpenForm: (type: keyof typeof import('../stubs').PRODUCT_WINDOW_TYPE) => void,
 *  onCloseForm: () => void,
 *  onSetInitialProducts: (products: Pick<import('zod').infer<typeof FORM_SCHEMA>, 'products'>) => void,
 *  onSetInitialProductTypes: (productTypes: any[]) => void
 * }>}
 */
const ProductReturnWindowConfigContext = React.createContext();

export function useProductReturnWindowConfig() {
  const context = React.useContext(ProductReturnWindowConfigContext);
  if (!context) {
    throw new Error(
      'useProductReturnWindowConfig must be used within a ProductReturnWindowConfigProvider'
    );
  }
  return context;
}

/**
 * @param {object} props
 * @param {string} props.windowId
 * @param {keyof typeof PRODUCT_WINDOW_TYPE} props.windowType
 * @param {() => void} props.onSaved
 * @param {React.ReactNode} props.children
 */
export function ProductReturnWindowConfigProvider({
  windowId = '',
  windowType = 'SKU',
  onSaved = () => {},
  children = null
}) {
  const { track } = useSegment();
  const isMountedRef = useRef(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(windowType);

  /** @type {import('utils/types').FormMode} */
  const mode = !!windowId ? 'edit' : 'create';

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const customValidation = useCallback(
    data => {
      const baseValidation = FORM_SCHEMA.safeParse(data);
      if (!baseValidation.success) {
        return baseValidation;
      }

      // Custom validation: require either product_types or product_variants
      if (selectedType === 'SKU') {
        const productVariants = data.product_variants || [];
        if (productVariants.length === 0) {
          return {
            success: false,
            error: {
              issues: [
                {
                  path: ['product_variants'],
                  message: 'Select at least one product variant'
                }
              ]
            }
          };
        }
      } else {
        const productTypes = data.product_types || [];
        if (productTypes.length === 0) {
          return {
            success: false,
            error: {
              issues: [
                {
                  path: ['product_types'],
                  message: 'Select at least one product type'
                }
              ]
            }
          };
        }
      }

      return baseValidation;
    },
    [selectedType]
  );

  const {
    data,
    initialData,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA,
    customValidation
  });

  const { isLoading, isError, fetchData: fetchDataOriginal } = useFetch(
    selectedType === 'SKU'
      ? 'configure.serviceWindow.exceptions.productSkus.root'
      : 'configure.serviceWindow.exceptions.productTypes.root',
    {
      loadOnMount: false,
      routeParam: windowId,
      initialData: DEFAULT_FORM_DATA,
      onTransform: createFormData,
      onSuccess: data => {
        if (isMountedRef.current) {
          onSetInitialData(data);
        }
      }
    }
  );

  // Wrap fetch to only call if mounted
  const onLoad = useCallback(() => {
    if (isMountedRef.current) {
      fetchDataOriginal();
    }
  }, [fetchDataOriginal]);

  const { mutate, isProcessing } = useMutation(
    selectedType === 'SKU'
      ? 'configure.serviceWindow.exceptions.productSkus.root'
      : 'configure.serviceWindow.exceptions.productTypes.root',
    mode === 'edit' ? 'PATCH' : 'POST',
    {
      routeParam: windowId,
      payload: () => createPayload(data, selectedType),
      successMessage: 'Successfully saved the return window.',
      errorMessage: (_, data) => {
        if (_isPlainObject(data)) {
          const firstErrorKey = Object.keys(data)[0];
          if (firstErrorKey && _isArray(data[firstErrorKey])) {
            return data[firstErrorKey][0];
          }
        }
        return 'Unable to save the return window at the moment.';
      },
      onValidate: () => onValidate(data),
      onSuccess: () => {
        if (isMountedRef.current) {
          if (mode === 'create') {
            track('FTU Wizard Product Return Window Created', {
              window_type: selectedType,
              days: data.service_days,
              product_variants_count:
                selectedType === 'SKU' ? data.product_variants?.length || 0 : 0,
              product_types_count:
                selectedType === 'TYPE' ? data.product_types?.length || 0 : 0
            });
          } else {
            track('FTU Wizard Product Return Window Edited', {
              window_id: windowId,
              window_type: selectedType,
              days: data.service_days,
              product_variants_count:
                selectedType === 'SKU' ? data.product_variants?.length || 0 : 0,
              product_types_count:
                selectedType === 'TYPE' ? data.product_types?.length || 0 : 0
            });
          }
          setIsFormOpen(false);
          onSaved();
        }
      }
    }
  );

  const onSetInitialProducts = useCallback(
    products => {
      if (mode !== 'edit') return;
      onSetInitialData({
        ...data,
        products,
        product_variants: getProductVariantIds(products)
      });
    },
    [data, mode, onSetInitialData]
  );

  const onSetInitialProductTypes = useCallback(
    productTypes => {
      if (mode !== 'edit') return;
      onSetInitialData({
        ...data,
        product_types: getProductTypeIds(productTypes)
      });
    },
    [data, mode, onSetInitialData]
  );

  const onSave = useCallback(() => mutate(), [mutate]);

  const onOpenForm = useCallback(
    /** @param {keyof typeof import('../stubs').PRODUCT_WINDOW_TYPE} type */
    type => {
      if (!isMountedRef.current) return;

      setSelectedType(type);
      setIsFormOpen(true);
      if (!!windowId) {
        onLoad();
      }
    },
    [windowId, onLoad]
  );

  const onCloseForm = useCallback(() => setIsFormOpen(false), []);

  return (
    <ProductReturnWindowConfigContext.Provider
      value={{
        windowId,
        mode,
        data,
        initialData,
        isError,
        isLoading,
        errors,
        isFormOpen,
        isDirty,
        isProcessing,
        selectedType,
        onSetInitialProducts,
        onSetInitialProductTypes,
        onChange,
        onLoad,
        onSave,
        onOpenForm,
        onCloseForm,
        onSaved
      }}
    >
      {children}
    </ProductReturnWindowConfigContext.Provider>
  );
}
